




import Enumerable from "linq";
import { Mixins, Component, Prop } from "vue-property-decorator";
import { ModelEditableMixin } from "../../../../../../base/components/system";
import { Point, Size, Rectangle, Vector } from "../../../../../../base/domain/drawing";
import { ObjectUtility } from "../../../../../../base/utilities";
import { DomainRegistry } from "../../../../../domain";
import { IFormatObjectEditable, IEditableFormatObject, FormatPageStructureEditor } from "../../../../../domain/editableFormat";
import { instanceOfIImageDrawableObject } from "../../../../../domain/editableFormat/objects";
import {
  instanceOfIVectorCoordinateEditable,
  instanceOfIRectangleCoordinateEditable,
  instanceOfILocationPropertyPresentable,
  instanceOfIScriptGroupKeyPropertyEditable,
  instanceOfIRangeOfMotionGroupKeyPropertyEditable,
} from "../../../../../domain/editableFormat/properties";
import { ScriptObjectEditor } from "../../../../../domain/editableFormat/objectEditors";
import { FormatPageStructureEditableMixin, QuestionnaireGuidAssignableMixin } from "../../mixins";
import { default as ObjectIdPublisher } from "./ObjectIdPublisher.vue";
import { default as ScriptGroupEditor } from "./ScriptGroupEditor.vue";
import { default as RangeOfMotionGroupEditor } from "./RangeOfMotionGroupEditor.vue";

@Component
export default class ClipboardManager extends Mixins(ModelEditableMixin, FormatPageStructureEditableMixin, QuestionnaireGuidAssignableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop()
  formatId: number;

  @Prop({ default: () => new Size(10, 10) })
  gridSize: Size;

  @Prop()
  objectIdPublisher: ObjectIdPublisher;

  @Prop()
  scriptGroupEditor: ScriptGroupEditor;

  @Prop()
  rangeOfMotionGroupEditor: RangeOfMotionGroupEditor;

  hasClippedObjects(): boolean {
    return (0 < DomainRegistry.instance.editableFormat.getClipboardRepository().getClippedObjects(this.formatId).length);
  }

  clipSelectedObjects(): void {
    const scriptGroupKeys = this.scriptGroupEditor.getSelectedGroupKeys();
    const rangeOfMotionGroupIds = this.rangeOfMotionGroupEditor.getSelectedGroupIds();
    this.clipObjects(this.extractSelectedObjects(s => (instanceOfIScriptGroupKeyPropertyEditable(s) && Enumerable.from(scriptGroupKeys).any(k => k === s.scriptGroupKey().getValue()))
      || (instanceOfIRangeOfMotionGroupKeyPropertyEditable(s) && Enumerable.from(rangeOfMotionGroupIds).any(i => i === this.rangeOfMotionGroupEditor.toRangeOfMotionGroupId(s.rangeOfMotionGroupKey().getValue())))));
  }

  private clipObjects(objects: IFormatObjectEditable[]): void {
    const clippedObjects = Enumerable.from(objects ? objects : [])
      .where(o => !!o)
      .select(o => this.toClippedObject(o.getSource()))
      .toArray();
    DomainRegistry.instance.editableFormat.getClipboardRepository().setClippedObjects(clippedObjects);
  }

  private toClippedObject(source: IEditableFormatObject): IEditableFormatObject {
    const result = ObjectUtility.copyDeeply(source) as IEditableFormatObject;
    result.id = undefined;      // idは採番し直すので破棄.
    result.nameHistories = [];  // 名称変更履歴はクリア.
    if (instanceOfIImageDrawableObject(result, result.elementName)) { // 画像は引き継がないようガード.(フォーマット毎に管理されてるので困難.v1もやってないので許容.)
      result.imageContent = {
        isEnable: false,
        guid: null,
      };
      result.temporaryImageContent = {
        isEnable: false,
        guid: null,
      };
    }
    return result;
  }

  async pasteObjects(location?: Point, index?: number): Promise<void> {
    try {
      const objects = await this.createNewObjectEditors(DomainRegistry.instance.editableFormat.getClipboardRepository().getClippedObjects(this.formatId), location);
      this.writePropertyWhenValid(s => {
        const guidDictionary = this.toNewGuidDictionaryForQuestionnaire(objects);
        const added = Enumerable.from(objects)
          .select((o, i) => {
              this.assignNewGuidForQuestionnaire(o, guidDictionary);  // Questionnaire拡張使用時GUIDをアサインする.
              return s.addObject(o, typeof index === "number" ? index + i : undefined);
            })
          .toArray();
        this.addQuestionnaireChildGuids(s, index, false, this.toRootGuidsForQuestionnaire(objects));
        this.selectObjects(...added);
      });
    } catch (e) {
      this.$logger.error("オブジェクト貼り付けを中止します.");
      this.$logger.error(e);
    }
  }

  private async createNewObjectEditors(objects: IEditableFormatObject[], offset?: Point): Promise<IFormatObjectEditable[]> {
    await this.confirmScriptRename(objects);
    const results = this.createNewObjectEditorInstances(objects);
    const locations = Enumerable.from(results)
      .select(e => instanceOfILocationPropertyPresentable(e) ? Point.from(e.location().getValue()) : null)
      .where(l => !!l)
      .toArray();
    const rectangle = Rectangle.from(locations);
    for (const editor of results) {
      this.moveObjectEditor(editor, offset ? offset.subtract(rectangle.topLeft) : this.gridSize.toPoint());
    }
    return results;
  }

  private async confirmScriptRename(objects: IEditableFormatObject[]): Promise<void> {
    const scriptGroupKeys = this.scriptGroupEditor.extractGroupKeys(objects);
    for (const key of scriptGroupKeys) {
      if (this.scriptGroupEditor.containsSameKey(key) && !(await this.scriptGroupEditor.confirmCreateUniqueKey())) {
        throw new Error("scriptオブジェクト名称重複.");
      }
    }
  }

  private createNewObjectEditorInstances(objects: IEditableFormatObject[]): IFormatObjectEditable[] {
    let nextRangeOfMotionGroupId = this.rangeOfMotionGroupEditor.createNewGroupId();
    const assignedRangeOfMotionGroupIds: { [id: number]: number } = {};
    return Enumerable.from(objects ? objects : [])
      .select(o => {
        const editor = this.createNewObjectEditor(o);
        // idは採番し直す.
        if (editor.id().isEditable()) {
          editor.id().setValue(this.objectIdPublisher.publish());
        }
        // scriptの名称はグループ識別子なので変更.(ユーザ確認はここまでに行われてる前提.)
        if (editor instanceof ScriptObjectEditor) {
          const key = editor.scriptGroupKey().getValue();
          if (this.scriptGroupEditor.containsSameKey(key)) {
            editor.scriptGroupKey().setValue(this.scriptGroupEditor.createUniqueKey(key));
          }
        }
        // 関節可動域の名称はグループ名で自動採番されるグループIDを含めた名前となるので変更.
        if (instanceOfIRangeOfMotionGroupKeyPropertyEditable(editor)) {
          const key = editor.rangeOfMotionGroupKey().getValue();
          const id = this.rangeOfMotionGroupEditor.toRangeOfMotionGroupId(key);
          const index = this.rangeOfMotionGroupEditor.toRangeOfMotionIndex(key);
          if (assignedRangeOfMotionGroupIds[id] === undefined) {
            assignedRangeOfMotionGroupIds[id] = nextRangeOfMotionGroupId++;
          }
          editor.rangeOfMotionGroupKey().setValue("graph_" + assignedRangeOfMotionGroupIds[id] + "[" + index + "]");
        }
        return editor;
      })
      .where(o => !!o)
      .toArray();
  }

  private createNewObjectEditor(source: IEditableFormatObject): IFormatObjectEditable {
    return source
      ? this.readPropertyOrDefault(s => s.createNewObject(ObjectUtility.copyDeeply(source)), null)
      : null;
  }

  private moveObjectEditor(source: IFormatObjectEditable, offset: Point): void {
    if (instanceOfIRectangleCoordinateEditable(source)) {
      source.location().setValue(Point.to(Point.from(source.location().getValue()).add(offset)));
    } else if (instanceOfIVectorCoordinateEditable(source)) {
      source.vector().setValue(Vector.to(Vector.from(source.vector().getValue()).move(offset)));
    }
  }
}
